import { useState } from "react";
import { WarningOctagon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PhoneVerify from "@/components/site/PhoneVerify";
import { api } from "@/lib/api";

// Delete my account: phone → code (scope delete) → start → type DELETE → confirm → done.
export default function DeleteFlow({ intro, phoneLabel, buttonLabel }) {
  const [token, setToken] = useState(null);
  const [start, setStart] = useState(null);
  const [typed, setTyped] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = async (fn) => { setBusy(true); setError(null); try { await fn(); } catch (e) { setError(e.code === "already_deleted" ? "This account has already been deleted." : e.message); } finally { setBusy(false); } };
  const onVerified = (t) => { setToken(t); run(async () => setStart(await api("/public/delete/start", { token: t, method: "POST" }))); };
  const confirm = (e) => { e.preventDefault(); run(async () => { await api("/public/delete/confirm", { token, body: { confirm_token: start.confirm_token } }); setDone(true); }); };

  if (done) return <p role="status" className="cw-tone-success rounded-2xl px-4 py-3 text-sm" data-testid="delete-done">Your account and its data have been deleted. Thank you for trying Crushwink.</p>;
  if (!token) return <PhoneVerify purpose="delete" onVerified={onVerified} intro={intro} phoneLabel={phoneLabel} buttonLabel={buttonLabel} />;
  if (error && !start) return <p role="alert" className="cw-tone-error rounded-2xl px-4 py-3 text-sm" data-testid="delete-error">{error}</p>;
  if (!start) return <p className="cw-muted text-sm">Checking…</p>;
  if (!start.account_found) return <p role="status" className="rounded-2xl bg-[var(--cw-surface-tertiary)] px-4 py-3 text-sm" data-testid="delete-no-account">{start.message}</p>;
  return (
    <form onSubmit={confirm} className="space-y-5" data-testid="delete-confirm-form">
      <div className="cw-tone-error flex gap-3 rounded-2xl p-4 text-sm leading-6" data-testid="delete-warning">
        <WarningOctagon size={20} className="mt-0.5 shrink-0" aria-hidden />
        <div><p className="font-semibold">Delete {start.first_name ? `${start.first_name}'s` : "this"} account for good?</p><p className="mt-1">{start.warning}</p></div>
      </div>
      <div><Label htmlFor="del-type">Type DELETE to confirm</Label>
        <Input id="del-type" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" className="mt-2 h-12 max-w-xs font-semibold tracking-widest" data-testid="delete-type-input" /></div>
      {error && <p role="alert" className="cw-error text-sm" data-testid="delete-error">{error}</p>}
      <Button type="submit" variant="destructive" disabled={busy || typed.trim() !== "DELETE"} className="h-12 rounded-full px-6" data-testid="delete-confirm-button">{busy ? "Deleting…" : "Delete my account"}</Button>
    </form>
  );
}
